import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  UNSAFE_RouteContext as RouteContext,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { requestBatchByNumber, scanFloorLabel } from "../../api/batches";
import { PageContentSkeleton } from "../common/PageSkeleton";
import {
  isBatchNumber,
  normalizeBatchNumberInput,
  swapBatchPathSegment,
} from "../../helpers/batchNumber";
import { looksLikeExternalRef } from "../../helpers/resolveScannerDestination";
import OrderHeldScreen from "./OrderHeldScreen";

const idsByNumber = new Map();

export const rememberBatchNumber = (number, id) => {
  if (number && id) idsByNumber.set(normalizeBatchNumberInput(number), String(id));
};

const BatchUrlContext = createContext("");

/** What the address bar calls this batch (its number when opened by number). */
export const useBatchUrlId = (fallbackId) =>
  useContext(BatchUrlContext) || String(fallbackId || "");

/** `/batches/{id}/…` + the in-batch link builder for the current URL. */
export const useBatchLink = (batchId) => {
  const urlId = useBatchUrlId(batchId);
  return (suffix = "") => `/batches/${encodeURIComponent(urlId)}${suffix}`;
};

/** Batch number, or an ERP work id / order number the server resolves by externalRef. */
const lookupKeyFor = (raw) => {
  if (isBatchNumber(raw)) return normalizeBatchNumberInput(raw);
  if (looksLikeExternalRef(raw)) return String(raw).trim();
  return "";
};

/**
 * Batch pages read `:id` with useParams and use it for API calls, Redux keys
 * and Firestore listeners, so it has to be the doc id. When the URL holds a
 * batch number (or an ERP work id), resolve it and hand the page the id while
 * the address bar keeps showing what was scanned. A work id whose order is
 * still held for labels renders the red Needs labels screen instead.
 */
export const withBatchNumberRoute = (Component, { missPath } = {}) => {
  const Wrapped = (props) => {
    const params = useParams();
    const route = useContext(RouteContext);
    const navigate = useNavigate();
    const raw = String(params?.id || "");
    const number = lookupKeyFor(raw);
    const [missed, setMissed] = useState("");
    const [held, setHeld] = useState(null);
    const [attempt, setAttempt] = useState(0);
    const [, setResolved] = useState(0);
    const id = number ? idsByNumber.get(number) || "" : raw;

    useEffect(() => {
      if (!number || idsByNumber.has(number)) return undefined;
      let cancelled = false;
      const miss = async () => {
        // Not a batch. An ERP work id may still name an order waiting on
        // labels; the floor scan route answers `kind: "order_held"` for it.
        if (looksLikeExternalRef(raw) && !isBatchNumber(raw)) {
          try {
            const res = await scanFloorLabel({ label: number });
            const payload = res?.data?.data || res?.data || res;
            if (cancelled) return;
            if (payload?.kind === "order_held") {
              setHeld({ ...payload, scanned: number });
              return;
            }
          } catch (_err) {
            // Fall through to the item page.
          }
        }
        if (!cancelled) setMissed(number);
      };
      requestBatchByNumber(number)
        .then((res) => {
          if (cancelled) return;
          const found = res?.data?.batch?.id;
          if (found) {
            rememberBatchNumber(number, found);
            setResolved((n) => n + 1);
          } else {
            miss();
          }
        })
        .catch(() => {
          if (!cancelled) miss();
        });
      return () => {
        cancelled = true;
      };
    }, [number, raw, attempt]);

    useEffect(() => {
      if (missPath && number && missed === number) {
        navigate(missPath(raw), { replace: true });
      }
    }, [missed, number, raw, navigate]);

    const routeValue = useMemo(() => {
      if (!number || !id || !route?.matches?.length) return route;
      const last = route.matches.length - 1;
      return {
        ...route,
        matches: route.matches.map((m, i) =>
          i === last ? { ...m, params: { ...m.params, id } } : m
        ),
      };
    }, [route, number, id]);

    const retryAfterRelease = () => {
      setHeld(null);
      setMissed("");
      // Ask the server again; the batch now exists for the ready lines.
      setAttempt((n) => n + 1);
    };

    if (held && held.scanned === number && !id) {
      return (
        <OrderHeldScreen
          held={held}
          scanned={number}
          onReleased={retryAfterRelease}
        />
      );
    }

    if (number && !id) {
      if (missed === number && !missPath) return <Component {...props} />;
      return <PageContentSkeleton titleWidth="180px" blocks={2} />;
    }

    return (
      <BatchUrlContext.Provider value={number || raw}>
        <RouteContext.Provider value={routeValue}>
          <Component {...props} />
        </RouteContext.Provider>
      </BatchUrlContext.Provider>
    );
  };
  Wrapped.displayName = `withBatchNumberRoute(${
    Component.displayName || Component.name || "Component"
  })`;
  return Wrapped;
};

/** A page opened by id switches the address bar to the number once it knows it. */
export const useShowBatchNumberInUrl = (batch) => {
  const urlId = useContext(BatchUrlContext);
  const location = useLocation();
  const navigate = useNavigate();
  const id = String(batch?.id || "");
  const number = batch?.batchNumber ? String(batch.batchNumber) : "";

  useEffect(() => {
    if (!id || !number || urlId !== id) return;
    rememberBatchNumber(number, id);
    const pathname = swapBatchPathSegment(location.pathname, id, number);
    if (pathname === location.pathname) return;
    navigate(
      { pathname, search: location.search, hash: location.hash },
      { replace: true, state: location.state }
    );
  }, [id, number, urlId, location, navigate]);
};
